/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Download, AlertCircle, CheckCircle, Loader, Info } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { SkillShopService } from '../../services/skillShopService';

type Step = 'filters' | 'preview' | 'importing' | 'complete';

interface LinearTeam {
  id: string;
  name: string;
  key: string;
}

interface LinearProject {
  id: string;
  name: string;
}

interface LinearUserStoryImportDialogProps {
  onClose: () => void;
  onImportComplete: () => void;
}

/**
 * Dialog for importing user stories from Linear.
 * 
 * Linear Projects are imported as Epics, and Linear Issues as User Stories.
 */
export function LinearUserStoryImportDialog({ onClose, onImportComplete }: LinearUserStoryImportDialogProps) {
  const { linearConfig, activeProduct } = useApp();

  const [step, setStep] = useState<Step>('filters');
  const [error, setError] = useState<string | null>(null);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teams, setTeams] = useState<LinearTeam[]>([]);
  const [projects, setProjects] = useState<LinearProject[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [skillShopService] = useState(() => new SkillShopService());

  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(
    linearConfig?.defaultTeamId ? [linearConfig.defaultTeamId] : []
  );
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [includeCompleted, setIncludeCompleted] = useState<boolean>(false);
  const [maxResults, setMaxResults] = useState<number>(100);
  const [updateExisting, setUpdateExisting] = useState(false);

  const hasLinearConfig = useMemo(() => !!linearConfig?.apiKey, [linearConfig]);

  useEffect(() => {
    if (!linearConfig?.apiKey) {
      setError('Linear integration is not configured. Please add an API key in Settings.');
      return;
    }

    let cancelled = false;
    const loadTeams = async () => {
      setLoadingTeams(true);
      setError(null);
      try {
        const response = await skillShopService.getLinearTeams({
          apiKey: linearConfig.apiKey,
          defaultTeamId: linearConfig.defaultTeamId || undefined
        });
        if (!cancelled) {
          if (response.success && response.data) {
            setTeams(response.data.teams || []);
            setProjects(response.data.projects || []);
          } else {
            setError(response.error || 'Failed to load Linear teams');
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Failed to load Linear teams:', err);
          setError(err.message || 'Failed to load Linear teams');
        }
      } finally {
        if (!cancelled) {
          setLoadingTeams(false);
        }
      }
    };

    loadTeams();

    return () => {
      cancelled = true;
    };
  }, [linearConfig, skillShopService]);

  const toggleTeamSelection = (teamId: string) => {
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      }
      return [...prev, teamId];
    });
  };

  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjectIds(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      }
      return [...prev, projectId];
    });
  };

  const loadPreview = async () => {
    if (!linearConfig?.apiKey) {
      setError('Linear API key is not configured.');
      return;
    }

    setLoadingPreview(true);
    setError(null);

    const result = await skillShopService.previewLinearUserStoryImport({
      apiKey: linearConfig.apiKey,
      defaultTeamId: linearConfig.defaultTeamId || undefined,
    }, {
      teamIds: selectedTeamIds.length ? selectedTeamIds : undefined,
      projectIds: selectedProjectIds.length ? selectedProjectIds : undefined,
      includeCompleted,
      maxResults,
    });

    if (result.success) {
      setPreview(result.data);
      setStep('preview');
    } else {
      setError(result.error || 'Failed to load preview');
    }
    
    setLoadingPreview(false);
  };

  const performImport = async () => {
    if (!linearConfig?.apiKey || !preview) {
      setError('Preview must be loaded before importing.');
      return;
    }

    setImporting(true);
    setError(null);
    setStep('importing');

    const result = await skillShopService.importLinearUserStories({
      apiKey: linearConfig.apiKey,
      defaultTeamId: linearConfig.defaultTeamId || undefined,
    }, {
      teamIds: selectedTeamIds.length ? selectedTeamIds : undefined,
      projectIds: selectedProjectIds.length ? selectedProjectIds : undefined,
      includeCompleted,
      maxResults,
      updateExisting,
      repoUrl: activeProduct?.repositoryUrl || undefined,
      repoPath: activeProduct?.repoPath || undefined,
    });

    if (result.success) {
      setImportResult(result.data);
      setStep('complete');
    } else {
      setError(result.error || 'Import failed');
      setStep('preview');
    }
    
    setImporting(false);
  };

  const handleComplete = () => {
    onImportComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Download className="w-5 h-5" />
            Import User Stories from Linear
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner about data mapping */}
        <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Import Mapping</p>
            <p className="text-sm text-blue-700 mt-1">
              Linear Projects will be imported as Epics, and Linear Issues as User Stories. 
              User story format will be parsed from issue descriptions when available.
            </p>
          </div>
        </div>

        {!hasLinearConfig && (
          <div className="p-6">
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-900 text-sm">
              Configure the Linear integration first by adding an API key in the Settings page.
            </div>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {hasLinearConfig && step === 'filters' && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Selection</label>
              {loadingTeams ? (
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Loader className="w-4 h-4 animate-spin" />
                  Loading teams...
                </div>
              ) : teams.length ? (
                <div className="grid grid-cols-2 gap-2">
                  {teams.map(team => (
                    <label key={team.id} className="flex items-center gap-2 text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedTeamIds.includes(team.id)}
                        onChange={() => toggleTeamSelection(team.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>{team.name} ({team.key})</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600">No teams available for this API key.</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Select teams to filter issues from.</p>
            </div>

            {projects.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Selection (as Epics)</label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {projects.map(project => (
                    <label key={project.id} className="flex items-center gap-2 text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.includes(project.id)}
                        onChange={() => toggleProjectSelection(project.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>{project.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Projects will be imported as Epics. Leave empty to import all.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Results</label>
                <input
                  type="number"
                  value={maxResults}
                  onChange={(e) => setMaxResults(Math.max(1, Math.min(250, Number(e.target.value))))}
                  min={1}
                  max={250}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeCompleted"
                  checked={includeCompleted}
                  onChange={(e) => setIncludeCompleted(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="includeCompleted" className="text-sm text-gray-700">
                  Include completed issues
                </label>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="updateExisting"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="updateExisting" className="text-sm text-gray-700">
                Update existing stories if found
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={loadPreview}
                disabled={loadingPreview || loadingTeams}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingPreview && <Loader className="w-4 h-4 animate-spin" />}
                {loadingPreview ? 'Loading Preview...' : 'Preview Import'}
              </button>
            </div>
          </div>
        )}

        {hasLinearConfig && step === 'preview' && preview && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Preview</h3>
              <p className="text-sm text-gray-600">
                Found {preview.projects?.length || 0} project{(preview.projects?.length || 0) !== 1 ? 's' : ''} (as epics) and {preview.issues?.length || 0} issue{(preview.issues?.length || 0) !== 1 ? 's' : ''} (as stories)
              </p>
            </div>

            {/* Projects as Epics */}
            {preview.projects?.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Projects → Epics</h4>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preview.projects.map((project: any) => (
                        <tr key={project.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{project.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{project.issueCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Issues as Stories */}
            {preview.issues?.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Issues → User Stories</h4>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preview.issues.map((issue: any) => (
                        <tr key={issue.identifier} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{issue.identifier}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{issue.title}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{issue.projectName || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{issue.state}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('filters')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={performImport}
                disabled={importing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {importing && <Loader className="w-4 h-4 animate-spin" />}
                {importing ? 'Importing...' : `Import ${(preview.projects?.length || 0) + (preview.issues?.length || 0)} Items`}
              </button>
            </div>
          </div>
        )}

        {hasLinearConfig && step === 'importing' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Importing user stories...</p>
            <p className="text-sm text-gray-600 mt-2">This may take a few moments</p>
          </div>
        )}

        {hasLinearConfig && step === 'complete' && importResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Import Complete!</h3>
              <p className="text-gray-600">
                Successfully imported {importResult.successful || 0} items
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{importResult.created || 0}</p>
                <p className="text-sm text-green-900">Created</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{importResult.updated || 0}</p>
                <p className="text-sm text-blue-900">Updated</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">{importResult.skipped || 0}</p>
                <p className="text-sm text-gray-900">Skipped</p>
              </div>
            </div>

            {importResult.errors?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Errors:</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResult.errors.map((errMsg: string, index: number) => (
                      <li key={index}>• {errMsg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

